/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RecentTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
  styled,
  Tooltip,
} from "@mui/material";

import CustomDataGridV2 from "../../../common/datagrid/CustomDataGridV2";
import {
  HandleApiActions,
  labCar,
  userManagementApis,
} from "../../../../../redux/actions";
import {
  ConvertUTCToDate,
} from "../../../../../utils/convertUtcToTimeZone";
import CustomButtonV2 from "../../../common/button/CustomButtonV2";
import CustomDialogBoxV2 from "../../../common/customDialogBox/CustomDialogBoxV2";
import { TICKETS_TYPES } from "../../../../../config/appConstants";
import ModuleViewMoreDialog from "./ModuleViewMoreDialog";
import softwareIntersectionAPI from "../../../../../redux/actions/softwareIntersectionActions";
import { componentStyle } from "../../../../../config/appTheme";
import CustomToolTip from "../../../../infoButton/CustomToolTip";

const ticketStatusNames = {
  101: "Draft",
  100: "New",
  150: "Confirm Legal Requirements",
  200: "Artifact Confirmation",
  300: "Evidence Submission",
  400: "Evidence Review",
  500: "Testing at TA reviewers",
  600: "Resolved",
  999: "Closed Incomplete",
};

const LinkButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.primary.main,
  textDecoration: "underline",
  padding: 0,
  minWidth: "auto",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
}));

const CountdownTimer = styled(Typography)({
  backgroundColor: "green",
  color: "white",
  borderRadius: 0,
  textAlign: "center",
  width: "100px",
  cursor: "pointer",
});

const formatSupplierName = (supplier) => {
  if (Array.isArray(supplier)) {
    return supplier.filter(Boolean).join(", ") || "-";
  }
  return supplier || "-";
};

function VehicleSystemCell({ row, supplier, onViewMore, formatSupplierName }) {
  const vsList = React.useMemo(() => {
    const raw =
      row?.vehicle_system_details ?? row?.allData?.vehicle_system_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_system_details, row?.allData?.vehicle_system_details]);

  const [selectedVsId, setSelectedVsId] = useState(
    vsList.length > 0 ? vsList[0]?.id : "",
  );

  useEffect(() => {
    if (
      vsList.length > 0 &&
      !vsList.some((vs) => vs?.id === selectedVsId)
    ) {
      setSelectedVsId(vsList[0]?.id);
    }
  }, [vsList, selectedVsId]);

  const handleVsChange = (e) => {
    setSelectedVsId(e.target.value);
  };

  if (vsList.length === 0) {
    return (
      <Box sx={{ textAlign: "left", fontSize: "14px", color: "gray", py: 1 }}>
        No Data Available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        ...componentStyle.font.tableText,
      }}
    >
      <Box sx={{ width: "100%", borderRadius: "0px" }}>
        <FormControl
          sx={{
            ...componentStyle.form.select.formControl,
            width: "160px",
          }}
        >
          <Select
            size="small"
            value={selectedVsId || (vsList.length > 0 ? vsList[0]?.id : "")}
            onChange={handleVsChange}
            displayEmpty
            fullWidth
            sx={{ ...componentStyle.form.select.select }}
            MenuProps={{
              PaperProps: {
                style: { width: "160px" },
                sx: { maxHeight: 300 },
              },
            }}
            renderValue={() => "Vehicle Systems"}
          >
            {vsList.map((vs, idx) => (
              <MenuItem
                sx={{ ...componentStyle.form.select.menuItem }}
                key={`vs-${vs?.id || idx}`}
                value={vs?.id}
              >
                <Box
                  sx={{
                    p: 0,
                    m: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Tooltip
                    title={formatSupplierName?.(
                      vs?.vs_supplier ??
                        vs?.supplier_name ??
                        vs?.supplier ??
                        vs?.intr_supplier,
                    ) || "-"}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Supplier Name:
                      </Box>{" "}
                      {formatSupplierName?.(
                        vs?.vs_supplier ??
                          vs?.supplier_name ??
                          vs?.supplier ??
                          vs?.intr_supplier,
                      ) || "-"}
                    </Typography>
                  </Tooltip>
                  {vs?.assembly_number && (
                    <Tooltip
                      title={vs.assembly_number}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Assembly No:
                        </Box>{" "}
                        {vs.assembly_number}
                      </Typography>
                    </Tooltip>
                  )}
                  <Tooltip title={vs?.name} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Name:
                      </Box>{" "}
                      {vs?.name || "-"}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    title={vs?.version}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Version:
                      </Box>{" "}
                      {vs?.version || "-"}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    title={vs?.description}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Description:
                      </Box>{" "}
                      {vs?.description || "-"}
                    </Typography>
                  </Tooltip>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {!supplier && vsList.length > 0 && (
        <Box sx={{ textAlign: "left", mt: 0.5 }}>
          <CustomButtonV2
            handleOnClick={() => {
              onViewMore?.(row?.allData || row, selectedVsId);
            }}
            size="small"
            btnName={"View More..."}
          />
        </Box>
      )}
    </Box>
  );
}

function VehicleComponentsCell({ row, supplier, onViewMore, formatSupplierName }) {
  const vsList = React.useMemo(() => {
    const raw =
      row?.vehicle_system_details ?? row?.allData?.vehicle_system_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_system_details, row?.allData?.vehicle_system_details]);

  const allVcList = React.useMemo(() => {
    const raw =
      row?.vehicle_component_details ?? row?.allData?.vehicle_component_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_component_details, row?.allData?.vehicle_component_details]);

  const [selectedVsId, setSelectedVsId] = useState(
    vsList.length > 0 ? vsList[0]?.id : "",
  );

  useEffect(() => {
    if (
      vsList.length > 0 &&
      !vsList.some((vs) => vs?.id === selectedVsId)
    ) {
      setSelectedVsId(vsList[0]?.id);
    }
  }, [vsList, selectedVsId]);

  const filteredVCs = React.useMemo(() => {
    if (!selectedVsId) return allVcList;
    return allVcList.filter((vc) => {
      const vcVsId = vc?.vs_id ?? vc?.pr_vs_id ?? vc?.vehicle_system_id;
      if (vcVsId != null) {
        return Number(vcVsId) === Number(selectedVsId);
      }
      return vsList.length <= 1;
    });
  }, [allVcList, selectedVsId, vsList.length]);

  const [selectedVcId, setSelectedVcId] = useState(
    filteredVCs.length > 0 ? filteredVCs[0]?.id : "",
  );

  useEffect(() => {
    if (filteredVCs.length > 0) {
      if (!filteredVCs.some((vc) => vc?.id === selectedVcId)) {
        setSelectedVcId(filteredVCs[0]?.id);
      }
    } else {
      setSelectedVcId("");
    }
  }, [filteredVCs, selectedVcId]);

  const handleVsChange = (e) => {
    const newVsId = e.target.value;
    setSelectedVsId(newVsId);
    const nextVCs = allVcList.filter((vc) => {
      const vcVsId = vc?.vs_id ?? vc?.pr_vs_id ?? vc?.vehicle_system_id;
      if (vcVsId != null) {
        return Number(vcVsId) === Number(newVsId);
      }
      return vsList.length <= 1;
    });
    setSelectedVcId(nextVCs.length > 0 ? nextVCs[0]?.id : "");
  };

  const handleVcChange = (e) => {
    setSelectedVcId(e.target.value);
  };

  if (vsList.length === 0 && allVcList.length === 0) {
    return (
      <Box sx={{ textAlign: "left", fontSize: "14px", color: "gray", py: 1 }}>
        No Data Available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        ...componentStyle.font.tableText,
      }}
    >
      {vsList.length > 0 && (
        <Box sx={{ width: "100%", borderRadius: "0px" }}>
          <FormControl
            sx={{
              ...componentStyle.form.select.formControl,
              width: "160px",
            }}
          >
            <Select
              size="small"
              value={selectedVsId || vsList[0]?.id}
              onChange={handleVsChange}
              displayEmpty
              fullWidth
              sx={{ ...componentStyle.form.select.select }}
              MenuProps={{
                PaperProps: {
                  style: { width: "160px" },
                  sx: { maxHeight: 300 },
                },
              }}
              renderValue={() => "Vehicle Systems"}
            >
              {vsList.map((vs, idx) => (
                <MenuItem
                  sx={{ ...componentStyle.form.select.menuItem }}
                  key={`vs-comp-${vs?.id || idx}`}
                  value={vs?.id}
                >
                  <Box
                    sx={{
                      p: 0,
                      m: 0,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip
                      title={formatSupplierName?.(
                        vs?.vs_supplier ??
                          vs?.supplier_name ??
                          vs?.supplier ??
                          vs?.intr_supplier,
                      ) || "-"}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Supplier Name:
                        </Box>{" "}
                        {formatSupplierName?.(
                          vs?.vs_supplier ??
                            vs?.supplier_name ??
                            vs?.supplier ??
                            vs?.intr_supplier,
                        ) || "-"}
                      </Typography>
                    </Tooltip>
                    {vs?.assembly_number && (
                      <Tooltip
                        title={vs.assembly_number}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            ...componentStyle.font.tableText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" fontWeight="bold">
                            Assembly No:
                          </Box>{" "}
                          {vs.assembly_number}
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip title={vs?.name} placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Name:
                        </Box>{" "}
                        {vs?.name || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vs?.version}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Version:
                        </Box>{" "}
                        {vs?.version || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vs?.description}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Description:
                        </Box>{" "}
                        {vs?.description || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {filteredVCs.length > 0 && (
        <Box sx={{ width: "100%", borderRadius: "0px" }}>
          <FormControl
            sx={{
              ...componentStyle.form.select.formControl,
              width: "160px",
            }}
          >
            <Select
              size="small"
              value={selectedVcId || filteredVCs[0]?.id}
              onChange={handleVcChange}
              displayEmpty
              fullWidth
              sx={{ ...componentStyle.form.select.select }}
              MenuProps={{
                PaperProps: {
                  style: { width: "160px" },
                  sx: { maxHeight: 300 },
                },
              }}
              renderValue={() => "Vehicle Components"}
            >
              {filteredVCs.map((vc, idx) => (
                <MenuItem
                  sx={{ ...componentStyle.form.select.menuItem }}
                  key={`vc-${vc?.id || idx}`}
                  value={vc?.id}
                >
                  <Box
                    sx={{
                      p: 0,
                      m: 0,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip
                      title={formatSupplierName?.(
                        vc?.vc_supplier ??
                          vc?.intr_supplier ??
                          vc?.supplier_name ??
                          vc?.supplier,
                      ) || "-"}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Supplier Name:
                        </Box>{" "}
                        {formatSupplierName?.(
                          vc?.vc_supplier ??
                            vc?.intr_supplier ??
                            vc?.supplier_name ??
                            vc?.supplier,
                        ) || "-"}
                      </Typography>
                    </Tooltip>
                    {vc?.component_number && (
                      <Tooltip
                        title={vc.component_number}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            ...componentStyle.font.tableText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" fontWeight="bold">
                            Component No:
                          </Box>{" "}
                          {vc.component_number}
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip title={vc?.name} placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Name:
                        </Box>{" "}
                        {vc?.name || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vc?.version}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Version:
                        </Box>{" "}
                        {vc?.version || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vc?.description}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Description:
                        </Box>{" "}
                        {vc?.description || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {!supplier && filteredVCs.length > 0 && (
        <Box sx={{ textAlign: "left", mt: 0.5 }}>
          <CustomButtonV2
            handleOnClick={() => {
              onViewMore?.(row?.allData || row, selectedVcId, selectedVsId);
            }}
            size="small"
            btnName={"View More..."}
          />
        </Box>
      )}
    </Box>
  );
}

const normalizeArray = (val) => {
  if (Array.isArray(val)) return val;
  return val ? [val] : [];
};

function RecentTickets({
  tickets_type = TICKETS_TYPES.EE,
  ticketDetails,
  hasTicketNeedMore,
}) {
  const dispatch = useDispatch();
  const profileDetails = useSelector((store) => store.profileReducer);
  const navigate = useNavigate();

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [page] = useState(1);
  const [rowsPerPage] = useState(25);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(5);
  const [intervalId, setIntervalId] = useState(null);
  const [userDetailsMap, setUserDetailsMap] = useState(new Map());
  const [legalDialogData, setLegalDialogData] = useState(null);
  const [isLegalDialogOpen, setIsLegalDialogOpen] = useState(false);
  const [isEEDialogOpen, setIsEEDialogOpen] = useState(false);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const roleType = profileDetails?.profile?.role_details?.role_type;
  const supplier = roleType === 2;

  const handleClickOpen = (ticket_id) => {
    setCurrentTicketId(ticket_id);
    setOpen(true);
    setTimer(5);
  };

  const handleClose = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
    }
  };

  const handleRedirect = useCallback(() => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (currentTicketId) {
      const linkType = currentTicketId.startsWith("INC")
        ? "incident.do"
        : "sn_customerservice_case.do";
      window.open(
        `https://ven07074.service-now.com/${linkType}?sysparm_query=number=${currentTicketId}`,
        "_blank",
      );
    }
  }, [intervalId, currentTicketId]);

  const toggleEEDialog = (newOpen) => () => {
    setIsEEDialogOpen(newOpen);
  };

  const handleSWMViewButtonClick = (item) => {
    const swmList = item?.swm_details;
    const swm = Array.isArray(swmList) ? swmList[0] : swmList;
    const eeList = item?.ee_details;
    const ee = Array.isArray(eeList) ? eeList[0] : eeList;

    setSelectedComponent(item);
    setApiAction(softwareIntersectionAPI.get_software_intersections);
    setLinkApiProps({
      swm_id: swm?.swm_id,
      ee_id: ee?.ee_id,
    });
    setApiDataKey("linked_swm_details");
    setModuleViewMoreType("SWM");
    toggleEEDialog(true)();
  };

  const handleEEViewButtonClick = (item) => {
    const eeList = item?.ee_details;
    const ee = Array.isArray(eeList) ? eeList[0] : eeList;

    setSelectedComponent(item);
    setApiAction(labCar.get_ee_intersections);
    setLinkApiProps({
      ee_id: ee?.ee_id,
    });
    setApiDataKey("linked_ee_details");
    setModuleViewMoreType("EE");
    toggleEEDialog(true)();
  };

  const handleVSViewButtonClick = (item, customVsId = null) => {
    const rawVs = item?.vehicle_system_details;
    let vsList = [];
    if (Array.isArray(rawVs)) {
      vsList = rawVs;
    } else if (rawVs) {
      vsList = [rawVs];
    }

    const vs = customVsId
      ? vsList.find((v) => Number(v?.id) === Number(customVsId)) || { id: customVsId }
      : vsList[0];

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_systems);
    setLinkApiProps({
      vs_id: vs?.id,
    });
    setApiDataKey("linked_vs_details");
    setModuleViewMoreType("VS");

    toggleEEDialog(true)();
  };

  const handleVCViewButtonClick = (item, customVcId = null, customVsId = null) => {
    const rawVc = item?.vehicle_component_details;
    let vcList = [];
    if (Array.isArray(rawVc)) {
      vcList = rawVc;
    } else if (rawVc) {
      vcList = [rawVc];
    }

    const rawVs = item?.vehicle_system_details;
    let vsList = [];
    if (Array.isArray(rawVs)) {
      vsList = rawVs;
    } else if (rawVs) {
      vsList = [rawVs];
    }

    const vc = customVcId
      ? vcList.find((v) => Number(v?.id) === Number(customVcId)) || { id: customVcId }
      : vcList[0];
    const vs = customVsId
      ? vsList.find((v) => Number(v?.id) === Number(customVsId)) || { id: customVsId }
      : vsList[0];

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_components);
    setLinkApiProps({
      vc_id: vc?.id,
      vs_id: vs?.id,
    });
    setApiDataKey("linked_vc_details");
    setModuleViewMoreType("VC");

    toggleEEDialog(true)();
  };

  const toggleLegalDialog = (newOpen) => () => {
    setIsLegalDialogOpen(newOpen);
  };

  const handleLEGALViewButtonClick = (item) => {
    const lr_details = normalizeArray(item?.lr_details);
    const ls_details = normalizeArray(item?.ls_details);

    const items2 = lr_details.map((lr) => ({
      lr_id: lr.lr_id,
      chapter: lr.chapter,
      description: lr.description,
      req_number: lr.req_number,
    }));

    const items3 = ls_details.map((ls) => ({
      ls_id: ls.ls_id,
      standard_number: ls.standard_number,
      name: ls.name,
      revision: ls.revision,
      description: ls.description,
      chapters: normalizeArray(ls.chapters),
    }));

    setLegalDialogData({
      heading2: `Legal Requirement`,
      items2: items2,
      items3: items3,
    });

    toggleLegalDialog(true)();
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      const apiDetails = { ...userManagementApis?.get_users_list_pa };
      delete apiDetails.action_type;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              page_size: 10000,
              page_number: 1,
            },
            show_toast: false,
          }),
        ),
      );

      if (!resp.error) {
        const users = resp?.data || [];
        const userMap = new Map(users?.map((user) => [user?.user_id, user]));
        setUserDetailsMap(userMap);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!open) return; // Prevent interval from creating when dialog is closed.

    setTimer(5); // Reset timer here so it’s set BEFORE interval starts.

    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, [open, handleRedirect]);

  const COLUMNS = [
    {
      field: "ticket_id",
      headerName: "Ticket Details",
      headerAlign: "center",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const renderDetails = (label, details) => (
          <Box
            sx={{
              marginBottom: "8px",
              p: 0,
              height: "100%",
            }}
          >
            <Box sx={{ fontWeight: "bold", mb: 1 }}>{label}</Box>
            {Object.entries(details).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  ...componentStyle.font.tableText,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {key.replaceAll("_", " ")}:{" "}
                <CustomToolTip
                  length={12}
                  style={{ ...componentStyle.font.tableText }}
                >
                  {value || "-"}
                </CustomToolTip>
              </Box>
            ))}
          </Box>
        );

        return (
          <Box
            sx={{
              textAlign: "left",
              ...componentStyle.font.tableText,

              p: 0,
              m: "-8px 0",
              minWidth: "100%",
            }}
          >
            {renderDetails("", {
              "Ticket Id": params?.value ? (
                <LinkButton
                  sx={{ ...componentStyle.font.tableText, mt: -0.5 }}
                  onClick={() => handleClickOpen(params.value)}
                >
                  {params?.value}
                </LinkButton>
              ) : (
                "-"
              ),
              "Intersection Id": params?.row?.intr_id || "-",
              Status: ticketStatusNames[params?.row?.status],
              "Created By":
                userDetailsMap?.get(params?.row?.created_by)?.fullname || "-",
              "Updated By": params?.row?.updated_user_details?.[0]?.name,
              Description: params?.row?.description,
              Reason: params?.row?.reason,
            })}

            <CustomDialogBoxV2
              open={open}
              onClose={handleClose}
              title={<Box></Box>}
            >
              <Box
                sx={{
                  width: "400px",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ ...componentStyle.font.tabLevelHeader }}
                >
                  Redirecting to ServiceNow Ticketing tool!
                </Typography>

                <CountdownTimer
                  onClick={handleRedirect}
                  sx={{
                    width: "fit-content",
                    mt: 1,
                    px: 2,
                    ...componentStyle.font.tabLevelHeader,
                    fontWeight: 500,
                  }}
                >
                  Redirecting in ({timer}) sec..
                </CountdownTimer>
              </Box>
            </CustomDialogBoxV2>
          </Box>
        );
      },
    },
    ...(Number(tickets_type) === TICKETS_TYPES.VEHICLE_SYSTEM
      ? [
          {
            field: "vehicle_system_details",
            headerName: "Vehicle System",
            headerAlign: "left",
            width: 180,
            renderCell: (params) => (
              <VehicleSystemCell
                row={params?.row}
                supplier={supplier}
                onViewMore={handleVSViewButtonClick}
                formatSupplierName={formatSupplierName}
              />
            ),
          },
          {
            field: "vehicle_component_details",
            headerName: "Vehicle Components",
            headerAlign: "left",
            width: 180,
            renderCell: (params) => (
              <VehicleComponentsCell
                row={params?.row}
                supplier={supplier}
                onViewMore={handleVCViewButtonClick}
                formatSupplierName={formatSupplierName}
              />
            ),
          },
        ]
      : [
          {
            field: "tickets_type",
            headerName: "Module Type",
            headerAlign: "left",
            minWidth: 180,
            flex:1,
            renderCell: (params) => {
              const swm_details = params?.row?.swm_details;
              const ee_details = params?.row?.ee_details;
              const vehicle_component_details = params?.row?.vehicle_component_details;

              const renderModuleContent = () => {
                if (
                  Array.isArray(swm_details) &&
                  swm_details.length > 0 &&
                  Number(params?.row?.ticket_type) === 1
                ) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={swm_details[0]?.swm_id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={() => "Software Modules"}
                          >
                            {swm_details.map((component, idx) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`swm-${component.swm_id || idx}`}
                                value={component.swm_id}
                              >
                                <Box sx={{ p: 0, m: 0, width: "100%", overflow: "hidden" }}>
                                  <Tooltip title={component.name} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <Box component="span" fontWeight="bold">Number:</Box> {component.name || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.version} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <Box component="span" fontWeight="bold">Version:</Box> {component.version || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.description} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                                      <Box component="span" fontWeight="bold">Description:</Box> {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {!supplier && (
                        <Box sx={{ textAlign: "left", marginTop: 2 }}>
                          <CustomButtonV2
                            handleOnClick={() => {
                              handleSWMViewButtonClick(params?.row?.allData);
                            }}
                            size="small"
                            btnName={"View More..."}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                }

                if (
                  Array.isArray(ee_details) &&
                  ee_details.length > 0 &&
                  Number(params?.row?.ticket_type) === 2
                ) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={ee_details[0]?.ee_id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={() => "EE Components"}
                          >
                            {ee_details.map((component, idx) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`ee-${component.ee_id || idx}`}
                                value={component.ee_id}
                              >
                                <Box sx={{ p: 0, m: 0, width: "100%", overflow: "hidden" }}>
                                  <Tooltip title={formatSupplierName(component?.intr_supplier ?? component?.supplier_name ?? component?.supplier)} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <Box component="span" fontWeight="bold">Supplier Name:</Box> {formatSupplierName(component?.intr_supplier ?? component?.supplier_name ?? component?.supplier)}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.assembly_number} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <Box component="span" fontWeight="bold">Assembly No:</Box> {component.assembly_number || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.version} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <Box component="span" fontWeight="bold">Version:</Box> {component.version || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.description} placement="top">
                                    <Typography variant="body2" sx={{ ...componentStyle.font.tableText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                                      <Box component="span" fontWeight="bold">Description:</Box> {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {params?.row?.allData?.dependencies?.ee_components &&
                        !supplier && (
                          <Box sx={{ textAlign: "left", marginTop: 2 }}>
                            <CustomButtonV2
                              handleOnClick={() => {
                                handleEEViewButtonClick(params?.row?.allData);
                              }}
                              size="small"
                              btnName={"View More..."}
                            />
                          </Box>
                        )}
                    </Box>
                  );
                }

                if (
                  Array.isArray(vehicle_component_details) &&
                  vehicle_component_details.length > 0
                ) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={vehicle_component_details[0]?.id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={() => "Vehicle Components"}
                          >
                            {vehicle_component_details.map((component, idx) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`vehicle-${component.id || idx}`}
                                value={component.id}
                              >
                                <Box sx={{ p: 0, m: 0, width: "100%", overflow: "hidden" }}>
                                  <Tooltip
                                    title={formatSupplierName(
                                      component?.vc_supplier ??
                                        component?.intr_supplier ??
                                        component?.supplier_name ??
                                        component?.supplier,
                                    )}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Supplier Name:
                                      </Box>{" "}
                                      {formatSupplierName(
                                        component?.vc_supplier ??
                                          component?.intr_supplier ??
                                          component?.supplier_name ??
                                          component?.supplier,
                                      )}
                                    </Typography>
                                  </Tooltip>
                                  {component?.component_number && (
                                    <Tooltip
                                      title={component.component_number}
                                      placement="top"
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          ...componentStyle.font.tableText,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Box component="span" fontWeight="bold">
                                          Component No:
                                        </Box>{" "}
                                        {component.component_number || "-"}
                                      </Typography>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={component.name} placement="top">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Name:
                                      </Box>{" "}
                                      {component.name || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.version}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Version:
                                      </Box>{" "}
                                      {component.version || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.description}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Description:
                                      </Box>{" "}
                                      {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {!supplier && (
                        <Box sx={{ textAlign: "left", marginTop: 2 }}>
                          <CustomButtonV2
                            handleOnClick={() => {
                              handleVCViewButtonClick(params?.row?.allData);
                            }}
                            size="small"
                            btnName={"View More..."}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                }

                return <Box sx={{ color: "gray" }}>No Data Available</Box>;
              };

              return (
                <Box
                  sx={{
                    textAlign: "left",
                    ...componentStyle.font.tableText,
                  }}
                >
                  {renderModuleContent()}
                </Box>
              );
            },
          },
        ]),

    {
      field: "ls_lr_details",
      headerName: "Legal Details",
      headerAlign: "left",
      minWidth: 200,
      flex: 1,

      renderCell: (params) => {
        const ls_details = params?.row?.ls_details;
        return (
          <Box sx={{ textAlign: "left", ...componentStyle.font.tableText }}>
            <Box sx={{ height: "90px", minHeight: "90px", overflowY: "auto", padding: "4px", borderRadius: "4px" }}>
              {Array.isArray(ls_details) && ls_details.length > 0 ? (
                ls_details.map((ls, idx) => (
                  <Box key={`ls-${ls?.ls_id || ls?.standard_number || idx}`} sx={{ mb: 1, pb: 1, borderBottom: idx < ls_details.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box><strong>Standard No:</strong> {ls?.standard_number || "-"}</Box>
                    <Box><strong>Name:</strong> {ls?.name || "-"}</Box>
                    <Box><strong>Revision:</strong> {ls?.revision || "-"}</Box>
                  </Box>
                ))
              ) : (
                <Box>
                  <Box><strong>Standard No:</strong> -</Box>
                  <Box><strong>Name:</strong> -</Box>
                  <Box><strong>Revision:</strong> -</Box>
                </Box>
              )}
            </Box>

            <Box sx={{ textAlign: "left", marginTop: 2 }}>
              <CustomButtonV2
                handleOnClick={() => {
                  handleLEGALViewButtonClick(params?.row?.allData);
                }}
                size="small"
                btnName={"View More..."}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "test_case_details",
      headerName: "Test Case Details",
      headerAlign: "left",
      width: 180,
      renderCell: (params) => {
        const testCaseDetails = params?.row?.test_case_details ?? [];
        const hasTestCases = testCaseDetails.length > 0;

        return (
          <Box sx={{ width: "100%", borderRadius: "0px" }}>
            <Box sx={{ width: "100%", paddingTop: "40px" }}>
              <FormControl
                sx={{
                  ...componentStyle.form.select.formControl,
                  width: "160px",
                }}
              >
                <Select
                  size="small"
                  value=""
                  displayEmpty
                  fullWidth
                  renderValue={() =>
                    hasTestCases ? "View Test Cases" : "No Test Cases"
                  }
                  sx={{ ...componentStyle.form.select.select }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                >
                  {testCaseDetails.map((testCase) => (
                    <MenuItem
                      key={testCase.manual_test_case_id}
                      value={testCase.manual_test_case_id}
                      sx={{ ...componentStyle.form.select.menuItem }}
                    >
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip
                          title={testCase?.manual_test_case_id}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              ID:
                            </Box>{" "}
                            {testCase?.manual_test_case_id ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.name} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Name:
                            </Box>{" "}
                            {testCase?.name ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.description} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%", // ensures ellipsis
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Description:
                            </Box>{" "}
                            {testCase?.description ?? "-"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        );
      },
    },

    {
      field: "users",
      headerName: "Assignee",
      headerAlign: "center",
      width: 180,
      renderCell: (params) => {
        const rowId = params?.row?.id || params?.row?.intr_id || "row";
        const techServiceUsers = params?.row?.tech_service_users || [];
        const oemUsers = params?.row?.oem_users || [];
        const supplierUsers = params?.row?.suppliers || [];
        const defaultUserDetails = {
          fullname: "User removed",
          email: "",
          role_details: {},
        };

        const techServiceUserOptions = techServiceUsers.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const oemUserOptions = oemUsers.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const supplierUserOptions = supplierUsers.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const techServiceDefault =
          techServiceUserOptions.length > 0
            ? techServiceUserOptions[0]?.id
            : "";
        const oemDefault =
          oemUserOptions.length > 0 ? oemUserOptions[0]?.id : "";

        const supplierDefault =
          supplierUserOptions.length > 0 ? supplierUserOptions[0]?.id : "";

        return (
          <FormControl
            sx={{ ...componentStyle.form.select.formControl, width: "160px" }}
          >
            {techServiceUserOptions.length > 0 && (
              <>
                <label
                  htmlFor={`compliance-reviewers-${rowId}`}
                  style={{ ...componentStyle.form.select.label }}
                >
                  Select Compliance Reviewers
                </label>
                <Select
                  id={`compliance-reviewers-${rowId}`}
                  inputProps={{ id: `compliance-reviewers-${rowId}` }}
                  value={techServiceDefault}
                  displayEmpty
                  sx={{ ...componentStyle.form.select.select }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                  renderValue={(selected) => {
                    const selectedUser = techServiceUserOptions.find(
                      (user) => user?.id === selected,
                    );
                    return selectedUser
                      ? selectedUser?.fullname
                      : "No Compliance reviewers";
                  }}
                >
                  {techServiceUserOptions.map((user) => (
                    <MenuItem
                      sx={{
                        ...componentStyle.form.select.menuItem,
                        "&:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },

                        "&.Mui-selected": {
                          backgroundColor: "#ebebeb !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                      key={`tech-${user?.id}`}
                      value={user?.id}
                    >
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        {user?.fullname}
                        <br />
                        {user?.email}
                        <br />
                        {user?.role_details?.role_name}
                        <br />
                        {user?.role_details?.department}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {oemUserOptions.length > 0 && (
              <>
                <label
                  htmlFor={`compliance-requestors-${rowId}`}
                  style={{
                    ...componentStyle.form.select.label,
                    marginTop:
                      techServiceUserOptions.length > 0 ? "10px" : "0",
                  }}
                >
                  Select Compliance Requestors
                </label>
                <Select
                  id={`compliance-requestors-${rowId}`}
                  inputProps={{ id: `compliance-requestors-${rowId}` }}
                  value={oemDefault}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                  displayEmpty
                  sx={{
                    ...componentStyle.form.select.select,
                  }}
                  renderValue={(selected) => {
                    const selectedUser = oemUserOptions.find(
                      (user) => user?.id === selected,
                    );
                    return selectedUser
                      ? selectedUser?.fullname
                      : "No TA requestors";
                  }}
                >
                  {oemUserOptions.map((user) => (
                    <MenuItem
                      sx={{
                        ...componentStyle.form.select.menuItem,
                        "&:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },

                        "&.Mui-selected": {
                          backgroundColor: "#ebebeb !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                      key={`oem-${user?.id}`}
                      value={user?.id}
                    >
                      {user?.fullname}
                      <br />
                      {user?.email}
                      <br />
                      {user?.role_details?.role_name}
                      <br />
                      {user?.role_details?.department}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {supplierUserOptions.length > 0 && (
              <>
                <label
                  htmlFor={`suppliers-${rowId}`}
                  style={{
                    ...componentStyle.form.select.label,
                    marginTop: supplierUserOptions.length > 0 ? "10px" : "0",
                  }}
                >
                  Suppliers
                </label>
                <Select
                  id={`suppliers-${rowId}`}
                  inputProps={{ id: `suppliers-${rowId}` }}
                  value={supplierDefault}
                  displayEmpty
                  sx={{
                    ...componentStyle.form.select.select,
                  }}
                  renderValue={(selected) => {
                    const selectedUser = supplierUserOptions.find(
                      (user) => user?.id === selected,
                    );
                    return selectedUser
                      ? selectedUser?.fullname
                      : "No Suppliers";
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                >
                  {supplierUserOptions.map((user) => (
                    <MenuItem
                      sx={{
                        ...componentStyle.form.select.menuItem,
                        "&:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },

                        "&.Mui-selected": {
                          backgroundColor: "#ebebeb !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                      key={`sup-${user?.id}`}
                      value={user?.id}
                    >
                      {user?.fullname}
                      <br />
                      {user?.email}
                      <br />
                      {user?.role_details?.role_name}
                      <br />
                      {user?.role_details?.department}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {techServiceUserOptions.length === 0 &&
              oemUserOptions.length === 0 &&
              supplierUserOptions.length === 0 && (
                <Typography sx={componentStyle.font.tableText}>
                  No Users Available
                </Typography>
              )}
          </FormControl>
        );
      },
    },

    {
      field: "updated_at",
      headerName: "Changed At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (ticketDetails) {
      const filteredTicketList = [...ticketDetails]
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .map((item, index) => ({
          ...item,
          id: (page - 1) * rowsPerPage + index + 1,
          intr_id: item?.intr_id,
          tech_service_users: item?.tech_service_users,
          oem_users: item?.oem_users,
          users: item?.tech_service_users || item?.oem_users,
          ticket_id: item?.ticket_id,
          tickets_type: item?.tickets_type,
          ticket_type: item?.ticket_type,
          status: item?.status,
          ticket_status: item?.ticket_id || item?.status,
          ls_details: item?.ls_details,
          lr_details: item?.lr_details,
          swm_details: item?.swm_details,
          vehicle_system_details: item?.vehicle_system_details,
          vehicle_component_details: item?.vehicle_component_details,
          ee_details: item?.ee_details,
          description: item?.description,
          created_at: ConvertUTCToDate(
            item?.created_at,
            profileDetails?.profile?.profile?.region,
          ),
          created_by: item?.created_by,
          updated_at: ConvertUTCToDate(
            item?.updated_at,
            profileDetails?.profile?.profile?.region,
          ),
          updated_by: item?.updated_by,
          allData: item,
        }));

      setTableRowsData(filteredTicketList);
    }
  }, [ticketDetails, page, rowsPerPage, profileDetails?.profile?.profile?.region]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  return (
    <Box sx={{ height: "450px", width: "100%" }}>
      <CustomDataGridV2
        rows={tableRowsData}
        columns={COLUMNS}
        rowCount={tableRowsData?.length}
        isMultiLine={true}
        showPagination={false}
        enableMore={hasTicketNeedMore}
        handleMore={() => {
          const getTicketType = () => {
            switch (Number(tickets_type)) {
              case TICKETS_TYPES.EE:
                return 0;
              case TICKETS_TYPES.SWM:
                return 1;
              case TICKETS_TYPES.VEHICLE_SYSTEM:
                return 2;
              case TICKETS_TYPES.VEHICLE_COMPONENT:
                return 3;
              default:
                return 0;
            }
          };
          const ticketType = getTicketType();

          navigate(
            `/dashboard/v2/type-approval/tickets?tab=${Number(ticketType) || 0}`,
          );
        }}
      />
      <ModuleViewMoreDialog
        isEEDialogOpen={isEEDialogOpen}
        toggleEEDialog={toggleEEDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
      />
      <CustomDialogBoxV2
        open={isLegalDialogOpen}
        onClose={toggleLegalDialog(false)}
        title={
          <Box
            sx={{
              fontWeight: "bold",
              color: "black",
              fontSize: "15px",
              display: "flex",
              width: "100%",
              justifyContent: "center",
              flex: 1,
            }}
          >
            Legal Details
          </Box>
        }
      >
        <Box sx={{ width: "650px" }}>
          <Box>
            {legalDialogData?.items3 && (
              <Box>
                {legalDialogData?.items3?.map((ls, index) => (
                  <Box
                    key={`ls-${ls?.ls_id || ls?.standard_number || index}`}
                    sx={{
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Standard
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: ls?.ls_id },
                        {
                          header: "Standard Number",
                          value: ls?.standard_number,
                        },
                        { header: "Name", value: ls?.name },
                        { header: "Revision", value: ls?.revision },
                        { header: "Description", value: ls?.description },
                        {
                          header: "Chapters",
                          value: Array.isArray(ls?.chapters)
                            ? ls.chapters.join(", ")
                            : ls?.chapters || "-",
                        },
                      ]?.map((det) => (
                        <Typography key={det.header} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box>
            {legalDialogData?.heading2 && (
              <Box>
                {legalDialogData?.items2?.map((lr, index) => (
                  <Box
                    key={`lr-${lr?.lr_id || lr?.req_number || index}`}
                    sx={{
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Requirements
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: lr?.lr_id },
                        { header: "Chapter", value: lr?.chapter },
                        {
                          header: "Requirement Number",
                          value: lr?.req_number,
                        },
                        {
                          header: "Description",
                          value: lr?.description,
                        },
                      ]?.map((det) => (
                        <Typography key={det.header} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

RecentTickets.propTypes = {
  tickets_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ticketDetails: PropTypes.array,
  hasTicketNeedMore: PropTypes.bool,
};

VehicleSystemCell.propTypes = {
  row: PropTypes.object,
  supplier: PropTypes.bool,
  onViewMore: PropTypes.func,
  formatSupplierName: PropTypes.func,
};

VehicleComponentsCell.propTypes = {
  row: PropTypes.object,
  supplier: PropTypes.bool,
  onViewMore: PropTypes.func,
  formatSupplierName: PropTypes.func,
};

export default RecentTickets;
